import { useEffect, useMemo } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { Star, ChevronsLeft, ChevronsRight, History, Circle } from "lucide-react";
import { V2_NAV, V2_NAV_FLAT } from "@/v2/nav";
import { useLocalPref } from "@/v2/hooks/useLocalPref";
import { useAuth } from "@/context/AuthContext";

function BrandMark({ size = 30 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: 8,
        background: "var(--v2-grad-brand)",
        display: "grid", placeItems: "center",
        color: "#fff",
        boxShadow: "0 6px 16px var(--v2-primary-glow), inset 0 1px 0 rgba(255,255,255,0.18)",
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width={size * 0.6} height={size * 0.6} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15 L10 6 L14 12 L20 4" />
        <path d="M4 20 L20 20" opacity="0.55" />
      </svg>
    </div>
  );
}

export function V2Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const role = user?.role || "guest";
  const { pathname } = useLocation();
  const [favorites, setFavorites] = useLocalPref("zj_v2_favs", []);
  const [recents, setRecents] = useLocalPref("zj_v2_recents", []);

  // Track recent pages
  useEffect(() => {
    const item = V2_NAV_FLAT.find((n) => pathname === n.to || pathname.startsWith(n.to + "/"));
    if (!item) return;
    setRecents((prev) => {
      const next = [item.key, ...prev.filter((k) => k !== item.key)].slice(0, 5);
      return next;
    });
  }, [pathname, setRecents]);

  const toggleFav = (key) => {
    setFavorites((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const currentKey = V2_NAV_FLAT.find((n) => pathname === n.to || pathname.startsWith(n.to + "/"))?.key;
  const favItems = useMemo(() => favorites.map((k) => V2_NAV_FLAT.find((n) => n.key === k)).filter(Boolean), [favorites]);
  const recentItems = useMemo(
    () => recents.map((k) => V2_NAV_FLAT.find((n) => n.key === k)).filter((it) => it && it.key !== currentKey && !favorites.includes(it.key)),
    [recents, currentKey, favorites]
  );

  return (
    <aside className={`v2-sidebar ${collapsed ? "collapsed" : ""}`} data-testid="v2-sidebar">
      {/* Brand */}
      <div style={{ padding: collapsed ? "18px 12px" : "18px 20px", borderBottom: "1px solid var(--v2-border)", display: "flex", alignItems: "center", gap: 10 }}>
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, textDecoration: "none" }}>
          <BrandMark size={collapsed ? 30 : 32} />
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "var(--v2-font-display)", fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1, color: "var(--v2-text)" }}>
                Zaroori Jini
              </div>
              <div className="v2-label-mono" style={{ marginTop: 3, fontSize: 9.5 }}>OMS · Enterprise</div>
            </div>
          )}
        </Link>
        <button
          className="v2-btn v2-btn-ghost v2-btn-icon"
          onClick={onToggle}
          data-testid="v2-sidebar-toggle"
          title={collapsed ? "Expand" : "Collapse"}
          style={{ padding: 6, width: 28, height: 28 }}
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
        </button>
      </div>

      {/* Scroll body */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0", overflowX: "hidden" }}>
        {/* Favorites */}
        {favItems.length > 0 && (
          <>
            <div className="v2-nav-section">Favorites</div>
            {favItems.map((it) => (
              <NavItem key={it.key} item={it} collapsed={collapsed} favorites={favorites} onToggleFav={toggleFav} />
            ))}
          </>
        )}

        {/* Recents */}
        {!collapsed && recentItems.length > 0 && (
          <>
            <div className="v2-nav-section" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <History size={11} /> Recent
            </div>
            {recentItems.slice(0, 3).map((it) => (
              <NavItem key={"r-" + it.key} item={it} collapsed={collapsed} favorites={favorites} onToggleFav={toggleFav} />
            ))}
          </>
        )}

        {/* Main groups (role-filtered) */}
        {V2_NAV.filter((g) => !g.role || g.role === role || role === "super_admin").map((group) => {
          const items = group.items.filter((it) => !it.role || it.role === role || role === "super_admin");
          if (items.length === 0) return null;
          return (
            <div key={group.section}>
              <div className="v2-nav-section">{group.section}</div>
              {items.map((it) => (
                <NavItem key={it.key} item={it} collapsed={collapsed} favorites={favorites} onToggleFav={toggleFav} />
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer badge */}
      {!collapsed && (
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--v2-border)", fontSize: 11, color: "var(--v2-text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
          <Circle size={7} strokeWidth={0} fill="#22C55E" />
          All systems normal
        </div>
      )}
    </aside>
  );
}

function NavItem({ item, collapsed, favorites, onToggleFav }) {
  const Icon = item.icon;
  const isFav = favorites.includes(item.key);
  return (
    <NavLink
      to={item.to}
      end={item.to === "/dashboard"}
      className={({ isActive }) => `v2-nav-item ${isActive ? "active" : ""}`}
      data-testid={`v2-nav-${item.key}`}
      title={collapsed ? item.label : undefined}
    >
      <Icon className="v2-nav-icon" strokeWidth={1.8} />
      <span className="v2-nav-label">{item.label}</span>
      {!collapsed && (
        <button
          type="button"
          className={`v2-fav-star ${isFav ? "active" : ""}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(item.key); }}
          title={isFav ? "Remove from favorites" : "Add to favorites"}
          data-testid={`v2-fav-${item.key}`}
          style={{ background: "transparent", border: 0, cursor: "pointer" }}
        >
          <Star size={12} fill={isFav ? "currentColor" : "none"} strokeWidth={1.8} />
        </button>
      )}
    </NavLink>
  );
}
